import React, { useState, useEffect } from 'react';
import { aiAPI } from '../../../services/api';
import { parseUTCTimestamp } from '../../../utils/dateUtils';

type ConversationSummary = {
  conversation_id: string;
  title: string | null;
  source: string;
  summary: string;
  updated_time: string | null;
};

export const ConversationContextViewer: React.FC = () => {
  const [data, setData] = useState<{
    rolling_synthesis: { content: string; updated_time: string | null } | null;
    conversation_summaries: ConversationSummary[];
  } | null>(null);
  const [selectedConvId, setSelectedConvId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await aiAPI.getAgentContext();
        if (!cancelled) {
          const summaries = result.conversation_summaries || [];
          setData({
            rolling_synthesis: result.rolling_synthesis,
            conversation_summaries: summaries,
          });
          if (summaries.length > 0) {
            setSelectedConvId(summaries[0].conversation_id);
          }
        }
      } catch {
        if (!cancelled) setError('Failed to load conversation context');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const formatDate = (dateStr: string) => {
    const date = parseUTCTimestamp(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const convLabel = (conv: ConversationSummary) => {
    const source = conv.source.charAt(0).toUpperCase() + conv.source.slice(1);
    const title = conv.title ? ` — ${conv.title}` : '';
    const date = conv.updated_time ? ` (${formatDate(conv.updated_time)})` : '';
    return `${source}${title}${date}`;
  };

  const selectedConv = data?.conversation_summaries.find(c => c.conversation_id === selectedConvId);

  const textBoxClass = "w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-48 overflow-y-auto";
  const emptyBoxClass = "w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic";

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Conversation Context
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        What the agent remembers across and within conversations. Rolling synthesis is cross-conversation; conversation summaries are per-chat.
      </p>

      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center">
          Loading...
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rolling Synthesis</div>
            {data?.rolling_synthesis ? (
              <div>
                <div className={textBoxClass}>{data.rolling_synthesis.content}</div>
                {data.rolling_synthesis.updated_time && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Updated: {formatDate(data.rolling_synthesis.updated_time)}
                  </p>
                )}
              </div>
            ) : (
              <div className={emptyBoxClass}>No rolling synthesis yet.</div>
            )}
          </div>

          <div>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Conversation Summaries ({data?.conversation_summaries.length || 0})
            </div>
            {data?.conversation_summaries && data.conversation_summaries.length > 0 ? (
              <div>
                <select
                  value={selectedConvId}
                  onChange={(e) => setSelectedConvId(e.target.value)}
                  className="w-full mb-2 px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300"
                >
                  {data.conversation_summaries.map((conv) => (
                    <option key={conv.conversation_id} value={conv.conversation_id}>
                      {convLabel(conv)}
                    </option>
                  ))}
                </select>
                {selectedConv && (
                  <div className={textBoxClass}>{selectedConv.summary}</div>
                )}
              </div>
            ) : (
              <div className={emptyBoxClass}>No active conversation summaries.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
